import { Box, Container, Divider } from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@mui/material";
import ExcelLogo from "assets/img/excel-logo.png";
import axios from "axios";
import {
  AutoCompleteComponent,
  BalanceCard,
  CurrencyDisplay,
  DateRangePicker,
  ModalError,
  Page,
  PrimaryButton,
  PrimaryTextButton,
  SearchTextInput,
  SelectInput,
  SkeletonComponent,
  StatusChip,
  TableAction,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import isEqual from "lodash/isEqual";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  formatDate,
  formatPayloadDate,
  getErrors,
  setFormatPayloadDate,
} from "utils";

const PaymentPage = () => {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const paramsStatus = urlParams?.get("status");
  const paramsComposer = urlParams?.get("composer_id");
  const paramsStart = urlParams?.get("start_date");
  const paramsEnd = urlParams?.get("end_date");

  const [loadingPage, setLoadingPage] = useState(true);
  const [optionComposer, setOptionComposer] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [tableParams, setTableParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    status: paramsStatus || "",
  });
  const [queryParams, setQueryParams] = useState({
    composer_id: Number(paramsComposer) || "",
    start_date: paramsStart
      ? setFormatPayloadDate(paramsStart)
      : moment().subtract(3, "months"),
    end_date: paramsEnd ? setFormatPayloadDate(paramsEnd) : moment(),
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [summaryDetail, setSummaryDetail] = useState({
    composer_balance: 0,
    composer_advance: 0,
  });
  const prevQueryParams = usePrevious(queryParams);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeTableParams = (value, key) => {
    setTableParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      [key]: value,
    }));
    handleChangePageParams(value, key);
  };
  const handleChangeSummaryDetail = (value, key) => {
    setSummaryDetail(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeStartDate = date => {
    handleChangeQueryParams(date, "start_date");
    handleChangePageParams(formatPayloadDate(date), "start_date");
  };
  const handleChangeEndDate = date => {
    handleChangeQueryParams(date, "end_date");
    handleChangePageParams(formatPayloadDate(date), "end_date");
  };

  const getOptionComposer = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction-composer`,
        { headers }
      );
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.composer_id,
        label: item?.composer_name,
      }));
      setOptionComposer(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getSummaryBalance = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/balance/composer`, {
        headers,
        params: {
          composer_id: queryParams?.composer_id,
          start_date: formatPayloadDate(queryParams?.start_date),
          end_date: formatPayloadDate(queryParams?.end_date),
        },
      });
      const { data } = res?.data;
      handleChangeSummaryDetail(data?.total, "composer_balance");
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getSummaryAdvance = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/payment/composer/advance`, {
        headers,
        params: {
          composer_id: queryParams?.composer_id,
          start_date: formatPayloadDate(queryParams?.start_date),
          end_date: formatPayloadDate(queryParams?.end_date),
        },
      });
      const { data } = res?.data;
      handleChangeSummaryDetail(data?.total, "composer_advance");
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/payment`,
        {
          headers,
          params: {
            ...tableParams,
            composer_id: queryParams?.composer_id,
            filter_start_date: formatPayloadDate(queryParams?.start_date),
            filter_end_date: formatPayloadDate(queryParams?.end_date),
          },
        }
      );
      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, tableParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams, tableParams]
  );
  const downloadTemplate = () => {
    const url = `${hardBaseUrl}/publisher/transaction-payment/export`;
    axios
      .get(url, {
        responseType: "blob",
        headers,
      })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "payment-transaction.csv");
        document.body.appendChild(link);
        link.click();
      })
      .catch(error => ModalError(getErrors(error?.response)));
  };

  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataTable()];
    if (
      (prevQueryParams && !isEqual(prevQueryParams, queryParams)) ||
      isFirstLoad?.current
    ) {
      getSummaryBalance();
      getSummaryAdvance();
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    getOptionComposer();
  }, []);
  useEffect(() => {
    if (prevQueryParams && !isEqual(prevQueryParams, queryParams)) {
      getSummaryBalance();
      getSummaryAdvance();
    }
    const isSearching = Boolean(tableParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, tableParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Payment">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Payment Transaction"
          breadcrumbData={breadcrumbData}
        />
        <ButtonGroupTop />
        <Box mb="16px">
          <Divider className={classes?.flatDivider} />
        </Box>
        <AutoCompleteComponent
          label="Composer/ Author"
          options={optionComposer}
          value={
            optionComposer.find(
              option => option.id === queryParams?.composer_id
            ) || null
          }
          onChange={value => handleChangeQueryParams(value, "composer_id")}
          size="small"
        />
        <Grid container spacing={1} my={2}>
          <Grid item>
            <BalanceCard
              title="Composer/ Author Balance"
              value={summaryDetail?.composer_balance}
              loading={loadingPage}
            />
          </Grid>
          <Grid item>
            <BalanceCard
              title="Composer/ Author Advance"
              value={summaryDetail.composer_advance}
              loading={loadingPage}
            />
          </Grid>
        </Grid>
        <PrimaryTextButton
          label="See Balance Mutation"
          onClick={() => history.push("/admin/payment-mutation")}
          endIcon={<ArrowForward />}
        />
        <Grid container justifyContent="space-between" mt={1} mb={3}>
          <Grid item>
            <Grid container columnSpacing={1}>
              <Grid item>
                <SearchTextInput
                  placeholder="Search"
                  value={tableParams?.search}
                  onChange={e =>
                    handleChangeTableParams(e?.target?.value, "search")
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1}>
              <Grid item>
                <DateRangePicker
                  label="Period"
                  startDate={queryParams?.start_date}
                  handleChangeStartDate={handleChangeStartDate}
                  endDate={queryParams?.end_date}
                  handleChangeEndDate={handleChangeEndDate}
                />
              </Grid>
              <Grid item>
                <SelectInput
                  label="Status"
                  value={tableParams?.status}
                  onChange={e =>
                    handleChangeTableParams(e?.target?.value, "status")
                  }
                  options={listStatus}
                  optionKey="id"
                  optionLabel="name"
                  placeholder="All"
                  width={150}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Download CSV"
                  startIcon={<img src={ExcelLogo} alt="logo" />}
                  onClick={downloadTemplate}
                  height={40}
                  size="large"
                  color="#34774C"
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Add Payment"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    history.push({ pathname: "/admin/payment/add" })
                  }
                  height={40}
                  size="large"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={tableParams?.page}
            rowsPerPage={tableParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeTableParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeTableParams(e?.target?.value, "size")
            }
            isHaveAction
            renderAction={item => (
              <TableAction
                handleView={() =>
                  history.push(
                    `/admin/payment/${item.publisher_transaction_id}`
                  )
                }
              />
            )}
          />
        )}
      </Container>
    </Page>
  );
};

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/payment",
  },
  {
    label: "Payment",
    active: true,
  },
];
const listStatus = [
  {
    id: "approved",
    name: "Approved",
    color: "success",
  },
  {
    id: "paid",
    name: "Paid",
    color: "success",
  },
  {
    id: "unpaid",
    name: "Unpaid",
    color: "danger",
  },
  {
    id: "waiting_for_approval",
    name: "Waiting For Approval",
    color: "warning",
  },
];
const columnTable = [
  {
    name: "composer_name",
    title: "Composer/Author Name",
  },
  {
    name: "invoice_date",
    title: "Date",
    renderText: item => formatDate(item, "DD MMMM YYYY"),
  },
  {
    name: "base_currency_revenue",
    title: "Value",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
  {
    name: "notes",
    title: "Notes",
    renderText: item => item || "-",
  },
  {
    name: "status",
    title: "Status",
    renderText: item => (
      <StatusChip
        label={listStatus?.find(value => value?.id === item)?.name || "-"}
        type={listStatus?.find(value => value?.id === item)?.color || ""}
      />
    ),
  },
];

export default PaymentPage;
